import React, { useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Box, Grid, TextField, Button, Typography, FormHelperText } from "@mui/material";
import "./MyEditor.css";
import {  createCountry } from "../../services";

interface ContentState {
  title: string;
  coverImageSrc: string;
  author?: string;
  content: string;
}

const Country: React.FC = () => {
  const [content, setContent] = useState<ContentState>({
    title: "",
    coverImageSrc: "",
    content: "",
  });

  const [loading, setLoading] = useState<boolean>(false);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({
    title: "",
    coverImageSrc: "",
    content: "",
  });

  const handleSubmit = async () => {
    // Validation for required fields
    let errors = {
      title: "",
      coverImageSrc: "",
      content: "",
    };

    if (!content.title.trim()) {
      errors.title = "Title is required!";
    }
    if (!content.coverImageSrc.trim()) {
      errors.coverImageSrc = "Cover Image URL is required!";
    }
    if (!content.content.trim()) {
      errors.content = "Content is required!";
    }

    // If there are any validation errors, update the state and return early
    if (Object.values(errors).some((error) => error !== "")) {
      setValidationErrors(errors);
      return;
    }

    // Clear previous errors if validation passed
    setValidationErrors({
      title: "",
      coverImageSrc: "",
      content: "",
    });

    setLoading(true);

    try {
      const response = await createCountry(content);
      if (response) {
        setContent({
          title: "",
          coverImageSrc: "",
          content: "",
        });
      } else {
        console.log("Failed to save content. Please try again.");
      }
    } catch (error) {
      console.error("Error saving content:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: "20px" }}>
      <Typography variant="h4" className="heading" gutterBottom>
        Editor
      </Typography>

      <Grid container spacing={3}>
        {/* Left Side */}
        <Grid item xs={12} lg={6}>
          <TextField
            fullWidth
            label="Title"
            variant="outlined"
            value={content.title}
            onChange={(e) => setContent({ ...content, title: e.target.value })}
            margin="normal"
            required
            type="text"
            error={!!validationErrors.title}
            helperText={validationErrors.title}
          />
          <TextField
            fullWidth
            label="Cover Image URL"
            variant="outlined"
            value={content.coverImageSrc}
            onChange={(e) =>
              setContent({ ...content, coverImageSrc: e.target.value })
            }
            margin="normal"
            required
            error={!!validationErrors.coverImageSrc}
            helperText={validationErrors.coverImageSrc}
          />
          <CKEditor
            editor={ClassicEditor}
            data={content.content}
            onChange={(event, editor) => {
              const data = editor.getData();
              setContent({ ...content, content: data });
            }}
            config={{
              toolbar: [
                "heading",
                "|",
                "bold",
                "italic",
                "link",
                "bulletedList",
                "numberedList",
                "undo",
                "redo",
              ],
            }}
          />
          {validationErrors.content && (
            <FormHelperText error sx={{ marginBottom: 2 }}>
              {validationErrors.content}
            </FormHelperText>
          )}
          
         
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? "Saving..." : "Save Content"}
          </Button>
        </Grid>

        {/* Right Side */}
        <Grid item xs={12} lg={6}>
          <Box className="preview-container">
            <Typography variant="h5" className="preview-heading">
              Content Preview
            </Typography>
            <div className="preview preview-content">
              <h4>Title:</h4>
              <p>{content.title}</p>
              <h4>Cover Image:</h4>
              {content.coverImageSrc ? (
                <img src={content.coverImageSrc} alt="Cover" />
              ) : (
                <p>No cover image added.</p>
              )}
              <h4>Content:</h4>
              <div dangerouslySetInnerHTML={{ __html: content.content }} />
            </div>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Country;
